import {useEffect} from 'react';

import {BenchlingQueryResult, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getPipelineType, PipelineType} from 'pipelines/PipelineTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {
  GET_PIPELINE_MODIFIED_AT_QUERY_DATA_BY_PIPELINE_TYPE,
  GET_PIPELINE_QUERY_VARIABLES_BY_PIPELINE_TYPE,
  PIPELINE_TYPE_TO_MODIFIED_AT_QUERY,
  PIPELINE_TYPE_TO_QUERY,
  PipelineModifiedAtQuery,
  PipelineQueryResult,
  PipelineQueryVariables,
} from './PollingConstants';

const PIPELINE_POLL_INTERVAL_MS = 500;

interface ModifiedAtInformation {
  modifiedAt: string | null;
  previousModifiedAt: string | null;
}
const modifiedAtGlobalState = makeUseGlobalClientState<ModifiedAtInformation>({
  modifiedAt: null,
  previousModifiedAt: null,
});

/**
 * Hook to poll for the input analysis or analysis template's `modifiedAt`. Returns both the previous value
 * of `modifiedAt` and the new value after polling.
 *
 * @package
 */
function usePollingModifiedAt<T extends PipelineType>(pipelineId: string): ModifiedAtInformation {
  const pipelineType = getPipelineType(pipelineId) as T;
  const [modifiedAtInformation, setModifiedAtInformation] =
    modifiedAtGlobalState.useGlobalClientState(pipelineId);
  const queryResult = useBenchlingQuery<PipelineModifiedAtQuery[T], PipelineQueryVariables[T]>(
    PIPELINE_TYPE_TO_MODIFIED_AT_QUERY[pipelineType],
    {
      variables: GET_PIPELINE_QUERY_VARIABLES_BY_PIPELINE_TYPE[pipelineType](pipelineId),
      pollInterval: PIPELINE_POLL_INTERVAL_MS,
      // Without this, onCompleted doesn't seem to be called each poll cycle for... some reason.
      renderCachedDataWhenRefetching: true,
      onCompleted: (result) => {
        const currentModifiedAt = modifiedAtInformation.modifiedAt;
        const data = GET_PIPELINE_MODIFIED_AT_QUERY_DATA_BY_PIPELINE_TYPE[pipelineType](result);
        const newModifiedAt = isInaccessible(data) ? null : data.modifiedAt;

        if (newModifiedAt !== currentModifiedAt) {
          setModifiedAtInformation({
            modifiedAt: newModifiedAt,
            previousModifiedAt: currentModifiedAt,
          });
        }
      },
    }
  );

  // limit polling to only when the Benchling tab is visible
  // temporarily stop polling when the tab is hidden
  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.hidden) {
        queryResult.stopPolling();
      } else {
        queryResult.startPolling(PIPELINE_POLL_INTERVAL_MS);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [queryResult]);

  return modifiedAtInformation;
}

/**
 * Hook to poll for an Analysis or Analysis Template.
 *
 * @package
 */
export function usePollingPipeline<T extends PipelineType = PipelineType>(
  pipelineId: string
): BenchlingQueryResult<PipelineQueryResult[T], PipelineQueryVariables[T]> {
  const pipelineType = getPipelineType(pipelineId) as T;
  const {modifiedAt, previousModifiedAt} = usePollingModifiedAt(pipelineId);

  const result = useBenchlingQuery<PipelineQueryResult[T], PipelineQueryVariables[T]>(
    PIPELINE_TYPE_TO_QUERY[pipelineType],
    {
      variables: GET_PIPELINE_QUERY_VARIABLES_BY_PIPELINE_TYPE[pipelineType](pipelineId),
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  useEffect(() => {
    // Don't refetch if `previousModifiedAt` is null because a change between the previous and current value
    // is not meaningful
    if (previousModifiedAt != null && previousModifiedAt !== modifiedAt) {
      result.refetch();
    }
    // We don't want to refetch when `result` changes, so it's explicitly not in the dependency array
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [previousModifiedAt, modifiedAt]);

  return result;
}

export const usePollingAnalysis = usePollingPipeline<PipelineType.ANALYSIS>;

export const usePollingAnalysisTemplate = usePollingPipeline<PipelineType.ANALYSIS_TEMPLATE>;
